import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { LazyMotion, domAnimation } from "framer-motion";
import { Toaster } from "sonner";
import { AuthProvider } from "./lib/auth";
import { LocaleProvider } from "./lib/i18n";
import ErrorBoundary from "./components/ErrorBoundary";
import SmoothScroll from "./components/SmoothScroll";
import Header from "./components/Header";
import Footer from "./components/Footer";

// Critical routes — kept in the main chunk for fast first paint on SEO pages.
import Home from "./pages/Home";
import Author from "./pages/Author";
import NovelsList from "./pages/NovelsList";
import NovelDetail from "./pages/NovelDetail";
import TrilogyMalo from "./pages/TrilogyMalo";
import TrilogyChaines from "./pages/TrilogyChaines";
import News from "./pages/News";

// Non-critical routes — lazy-loaded, only fetched when the user navigates to them.
const Press = lazy(() => import("./pages/Press"));
const Publishers = lazy(() => import("./pages/Publishers"));
const CinemaProject = lazy(() => import("./pages/CinemaProject"));
const SampleChapters = lazy(() => import("./pages/SampleChapters"));
const Contact = lazy(() => import("./pages/Contact"));
const Ecosystem = lazy(() => import("./pages/Ecosystem"));
const JournalClimat = lazy(() => import("./pages/JournalClimat"));
const JournalClimatEn = lazy(() => import("./pages/en/JournalClimatEn"));
const HomeEn = lazy(() => import("./pages/en/HomeEn"));
const AuthorEn = lazy(() => import("./pages/en/AuthorEn"));
const NovelsListEn = lazy(() => import("./pages/en/NovelsListEn"));
const NovelDetailEn = lazy(() => import("./pages/en/NovelDetailEn"));
const NovelFirstChaptersEn = lazy(() => import("./pages/en/NovelFirstChaptersEn"));
const NewsListEn = lazy(() => import("./pages/en/NewsListEn"));
const NewsDetailEn = lazy(() => import("./pages/en/NewsDetailEn"));
const ContactEn = lazy(() => import("./pages/Contact").then((m) => ({ default: m.ContactEn })));
const EcosystemEn = lazy(() => import("./pages/en/EcosystemEn"));
const PressEn = lazy(() => import("./pages/en/PressEn"));
const PublishersEn = lazy(() => import("./pages/en/PublishersEn"));
const NotFoundEn = lazy(() => import("./pages/en/NotFoundEn"));
const NotFound = lazy(() => import("./pages/NotFound"));
const NewsletterConfirmation = lazy(() => import("./pages/NewsletterConfirmation"));
const NewsletterUnsubscribed = lazy(() => import("./pages/NewsletterUnsubscribed"));
const LegalMentions = lazy(() => import("./pages/Legal").then((m) => ({ default: m.LegalMentions })));
const Privacy = lazy(() => import("./pages/Legal").then((m) => ({ default: m.Privacy })));
const Cookies = lazy(() => import("./pages/Legal").then((m) => ({ default: m.Cookies })));
const LegalMentionsEn = lazy(() => import("./pages/Legal").then((m) => ({ default: m.LegalMentionsEn })));
const PrivacyEn = lazy(() => import("./pages/Legal").then((m) => ({ default: m.PrivacyEn })));
const CookiesEn = lazy(() => import("./pages/Legal").then((m) => ({ default: m.CookiesEn })));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const Prospection = lazy(() => import("./pages/admin/Prospection"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
    </>
  );
}

const RouteFallback = () => <div className="theme-ivory min-h-screen" />;

function AppRoutes() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
          <Route path="/auteur" element={<PublicLayout><Author /></PublicLayout>} />
          <Route path="/romans" element={<PublicLayout><NovelsList /></PublicLayout>} />
          <Route path="/romans/malo" element={<PublicLayout><TrilogyMalo /></PublicLayout>} />
          <Route path="/romans/les-chaines-du-vivant" element={<PublicLayout><TrilogyChaines /></PublicLayout>} />
          {/* 301 redirect from legacy Malo T1 URL (old trilogy name) */}
          <Route
            path="/romans/malo-les-routes-de-l-orient"
            element={<Navigate to="/romans/malo-les-routes-secretes-de-locean-indien/tome-1" replace />}
          />
          <Route
            path="/romans/malo-les-routes-de-l-orient/tome-1"
            element={<Navigate to="/romans/malo-les-routes-secretes-de-locean-indien/tome-1" replace />}
          />
          {/* 301 redirect from Malo parent slugs (no /tome-N) → tome-specific */}
          <Route
            path="/romans/malo-les-routes-secretes-de-locean-indien"
            element={<Navigate to="/romans/malo-les-routes-secretes-de-locean-indien/tome-1" replace />}
          />
          <Route
            path="/romans/malo-la-route-de-lest"
            element={<Navigate to="/romans/malo-la-route-de-lest/tome-2" replace />}
          />
          <Route
            path="/romans/malo-la-route-du-soleil-levant"
            element={<Navigate to="/romans/malo-la-route-du-soleil-levant/tome-3" replace />}
          />
          {/* NeuroAurum v2 canonical slug migration (2026-07-23) */}
          <Route
            path="/romans/les-chaines-du-vivant-neuroaurum"
            element={<Navigate to="/romans/les-chaines-du-vivant-tome-1-neuroaurum" replace />}
          />
          <Route
            path="/romans/les-chaines-du-vivant-neuroaurum/premiers-chapitres"
            element={<Navigate to="/romans/les-chaines-du-vivant-tome-1-neuroaurum/premiers-chapitres" replace />}
          />
          {/* Malo trilogy: canonical URL includes /tome-{N}. RR v7 does not
              support "tome-:tomeNum" (param inside segment with static prefix),
              so we use a wildcard segment and let NovelDetail parse it. */}
          <Route path="/romans/:slug/:tomePath" element={<PublicLayout><NovelDetail /></PublicLayout>} />
          {/* Public reading page: sample chapters of a novel */}
          <Route path="/romans/:novelPageSlug/premiers-chapitres" element={<PublicLayout><SampleChapters /></PublicLayout>} />
          <Route path="/romans/:slug" element={<PublicLayout><NovelDetail /></PublicLayout>} />
          <Route path="/actualites" element={<PublicLayout><News /></PublicLayout>} />
          <Route path="/journal-climat" element={<PublicLayout><JournalClimat /></PublicLayout>} />
          <Route path="/presse" element={<PublicLayout><Press /></PublicLayout>} />
          <Route path="/ecosysteme" element={<PublicLayout><Ecosystem /></PublicLayout>} />
          <Route path="/editeurs-agents" element={<PublicLayout><Publishers /></PublicLayout>} />
          <Route path="/cinema" element={<Navigate to="/cinema/protocole-zero" replace />} />
          <Route path="/cinema/protocole-zero" element={<PublicLayout><CinemaProject locale="fr" /></PublicLayout>} />
          <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
          <Route path="/mentions-legales" element={<PublicLayout><LegalMentions /></PublicLayout>} />
          <Route path="/confidentialite" element={<PublicLayout><Privacy /></PublicLayout>} />
          <Route path="/cookies" element={<PublicLayout><Cookies /></PublicLayout>} />
          {/* Newsletter — pages de confirmation/désinscription (redirection depuis /api/newsletter/*) */}
          <Route path="/newsletter/merci" element={<PublicLayout><NewsletterConfirmation locale="fr" /></PublicLayout>} />
          <Route path="/newsletter/desinscription" element={<PublicLayout><NewsletterUnsubscribed locale="fr" /></PublicLayout>} />
          <Route path="/en/newsletter/thank-you" element={<PublicLayout><NewsletterConfirmation locale="en" /></PublicLayout>} />
          <Route path="/en/newsletter/unsubscribed" element={<PublicLayout><NewsletterUnsubscribed locale="en" /></PublicLayout>} />
          {/* English routes (LOT 3A + 3B: home, author, novels list & details, institutional) */}
          <Route path="/en" element={<PublicLayout><HomeEn /></PublicLayout>} />
          <Route path="/en/author" element={<PublicLayout><AuthorEn /></PublicLayout>} />
          <Route path="/en/novels" element={<PublicLayout><NovelsListEn /></PublicLayout>} />
          <Route path="/en/novels/:slug" element={<PublicLayout><NovelDetailEn /></PublicLayout>} />
          <Route path="/en/novels/:slug/first-chapters" element={<PublicLayout><NovelFirstChaptersEn /></PublicLayout>} />
          <Route path="/en/news" element={<PublicLayout><NewsListEn /></PublicLayout>} />
          <Route path="/en/news/:slug" element={<PublicLayout><NewsDetailEn /></PublicLayout>} />
          <Route path="/en/climate-journal" element={<PublicLayout><JournalClimatEn /></PublicLayout>} />
          <Route path="/en/contact" element={<PublicLayout><ContactEn /></PublicLayout>} />
          <Route path="/en/ecosystem" element={<PublicLayout><EcosystemEn /></PublicLayout>} />
          <Route path="/en/press" element={<PublicLayout><PressEn /></PublicLayout>} />
          <Route path="/en/publishers-agents" element={<PublicLayout><PublishersEn /></PublicLayout>} />
          <Route path="/en/film" element={<Navigate to="/en/film/protocol-zero" replace />} />
          <Route path="/en/film/protocol-zero" element={<PublicLayout><CinemaProject locale="en" /></PublicLayout>} />
          <Route path="/en/legal-notice" element={<PublicLayout><LegalMentionsEn /></PublicLayout>} />
          <Route path="/en/privacy-policy" element={<PublicLayout><PrivacyEn /></PublicLayout>} />
          <Route path="/en/cookies" element={<PublicLayout><CookiesEn /></PublicLayout>} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/admin/prospection" element={<Prospection />} />
          {/* LOT 3E — Catch-all for unknown EN routes (dedicated EN 404) */}
          <Route path="/en/*" element={<PublicLayout><NotFoundEn /></PublicLayout>} />
          {/* FR catch-all — any unknown French URL renders the FR 404 instead of a blank page */}
          <Route path="*" element={<PublicLayout><NotFound /></PublicLayout>} />
        </Routes>
      </Suspense>
      {!isAdmin && <div className="paper-grain fixed inset-0 pointer-events-none z-[1]" />}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <LazyMotion features={domAnimation} strict>
          <AuthProvider>
            <BrowserRouter>
              <LocaleProvider>
                <SmoothScroll>
                  <div className="App font-body">
                    <Toaster position="bottom-right" richColors />
                    <AppRoutes />
                  </div>
                </SmoothScroll>
              </LocaleProvider>
            </BrowserRouter>
          </AuthProvider>
        </LazyMotion>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
